import React, { useEffect, useMemo } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { Info } from 'lucide-react';
import { z } from 'zod';

import Button from '../../common/Button';
import { toastPromise } from '../../common/utils';
import {
  useCreateUserDefinedExpense,
  useGetPreDefinedExpenses,
  useGetUserDefinedExpenses,
} from '../../hooks/tanstack/useExpenseHook';
import {
  useCustomNavigate,
  useGetRouteParams,
} from '../../hooks/tanstack/useUtilsHook';
import useExpenseStore from '../../hooks/zustand/useExpenseStore';
import routes from '../../routes';
import FormInput from '../form/FormInput';
import FormSelect from '../form/FormSelect';

type CreateScenarioFormDialogProps = {
  open: boolean;
  title: string;
  handleClose: () => void;
  defaultValues?: {
    name?: string;
    category?: string;
    description?: string;
    isCustom?: boolean;
  };
};

const CreateScenarioSchema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
  group: z.number({ message: 'Group is required' }),
  description: z.string().trim().optional(),
  isCustom: z.boolean().optional(),
});

export type AddExpenseFormSchemaType = z.infer<typeof CreateScenarioSchema>;

const AddExpenseFormDialog = ({
  open,
  title,
  defaultValues,
  handleClose,
}: CreateScenarioFormDialogProps) => {
  const { orgId, projectId } = useGetRouteParams();

  const navigate = useCustomNavigate();

  const form = useForm<AddExpenseFormSchemaType>({
    resolver: zodResolver(CreateScenarioSchema),
    defaultValues: {
      name: '',
      group: NaN,
      description: '',
      isCustom: false,
    },
  });

  const { handleSubmit, reset } = form;

  const defaultExpenseCategory = useExpenseStore(
    (state) => state.defaultExpenseCategory
  );

  const setDefaultExpenseCategory = useExpenseStore(
    (state) => state.setDefaultExpenseCategory
  );

  const { data: preDefinedExpenseSets } = useGetPreDefinedExpenses(
    {
      orgId,
    },
    {
      enabled: !!orgId,
    }
  );

  const { data: userDefinedExpenseSets } = useGetUserDefinedExpenses(
    {
      orgId,
      projectId,
    },
    {
      enabled: !!orgId && !!projectId,
    }
  );

  const groups = useMemo(
    () =>
      Array.from(new Set(preDefinedExpenseSets?.map((p) => p.group.name)))
        .map((e) => {
          const expense = preDefinedExpenseSets?.find(
            (g) => g.group.name === e
          );

          if (!expense) {
            return null;
          }

          return {
            label: expense.group.name,
            value: expense.group.id,
          };
        })
        .filter((e) => e) as { label: string; value: number }[],
    [preDefinedExpenseSets]
  );

  const defaultGroup =
    groups.find(
      (g) => g.label === (defaultValues?.category ?? defaultExpenseCategory)
    )?.value ?? groups[0]?.value;

  const {
    mutateAsync: createCustomExpense,
    isPending: isCreatingCustomExpense,
  } = useCreateUserDefinedExpense({
    onSuccess: (data) => {
      navigate(routes.projects.expenses.detail, {
        org_id: orgId,
        project_id: projectId,
        expense_id: data.id,
      });
      handleClose();
      reset();
    },
  });

  useEffect(() => {
    reset({
      ...defaultValues,
      group: defaultGroup,
    });
  }, [defaultValues, defaultGroup]);

  const handleCloseDialog = () => {
    handleClose();
    setDefaultExpenseCategory(null);
    reset();
  };

  const submit: SubmitHandler<AddExpenseFormSchemaType> = (data) => {
    const expenses = userDefinedExpenseSets?.concat(
      preDefinedExpenseSets as any
    );

    const existingExpense = expenses
      ?.filter((e) => !e.is_locked)
      ?.find(
        (e: {
          name: string;
          group: {
            id: number;
          };
        }) => e.name === data.name && e.group.id === data.group
      );

    if (existingExpense) {
      form.setError('name', {
        type: 'custom',
        message: 'Expense already exists in this category',
      });
      return;
    }

    const res = createCustomExpense({
      orgId,
      projectId,
      data: {
        ...data,
        formula: '0',
        project: projectId,
      },
    });

    toastPromise({
      promise: res,
      content: 'Custom expense created successfully',
    });
  };

  return (
    <>
      <Dialog
        open={open}
        disableEscapeKeyDown={isCreatingCustomExpense}
        onClose={!isCreatingCustomExpense ? handleCloseDialog : undefined}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className="w-full">
          <h2 className="px-6 pt-2 text-2xl font-bold">{title}</h2>
        </DialogTitle>
        <DialogContent>
          <div className="z-50 px-6">
            <FormProvider {...form}>
              <form onSubmit={handleSubmit(submit)} className="mb-2 space-y-6">
                <div className="space-y-2">
                  <FormInput
                    label="Name"
                    name="name"
                    fullWidth
                    disabled={isCreatingCustomExpense}
                  />
                  {groups && (
                    <FormSelect
                      name="group"
                      label="Category"
                      disabled={
                        !!defaultValues?.category || isCreatingCustomExpense
                      }
                      options={groups}
                      defaultValue={0}
                    />
                  )}
                  <FormInput
                    label="Description"
                    name="description"
                    placeholder="(Optional)"
                    disabled={isCreatingCustomExpense}
                    multiline
                    rows={4}
                  />
                </div>
                <div className="flex gap-2 items-center">
                  <div>
                    <Info size={20} color="#666666" />
                  </div>
                  <p className="text-sm">
                    To use the newly added Custom Expense, return to your
                    model's Configuration page and edit the Expense setting to
                    apply it.
                  </p>
                </div>
                <div className="flex-end flex justify-end gap-3">
                  <Button
                    disabled={isCreatingCustomExpense}
                    onClick={handleCloseDialog}
                    variant="outlined"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isCreatingCustomExpense}
                    loading={isCreatingCustomExpense}
                    variant="contained_blue"
                  >
                    {defaultValues ? 'Save' : 'Create'}
                  </Button>
                </div>
              </form>
            </FormProvider>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddExpenseFormDialog;
